import { memo } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { CookieNames } from 'constants/app';
import { mobileComponents } from 'constants/branding';
import { NumberOfPairs } from 'constants/marketTypes';
import { getIsMobile, getNumberOfMarketSelectionsScroll } from 'redux/modules/appConfigs/selectors';
import { TMarketResponse } from 'redux/modules/market/type';
import { getIsMarketClosed, getIsMarketSuspended } from 'redux/modules/marketsPrices/selectors';
import { IMarket, MarketActionsRowClasses } from 'types/markets';
import { getMarketTypes } from 'utils/market';

import styles from './styles.module.scss';

interface MarketActionsRowProps {
  /**
   * Market
   */
  market: IMarket | TMarketResponse;
  /**
   * Show all runners?
   */
  showAll?: boolean;
  /**
   * Callback fired to show all runners
   */
  onShowAllRunners: () => void;
  /**
   * Callback fired to minimize
   */
  onMinimize: () => void;
  /**
   * Classes for component customization
   */
  classes?: MarketActionsRowClasses;
  isShowPlTable: boolean;
  onTogglePLTable: () => void;
  isOpeningSoonStatus?: boolean;
  ignoreActions?: boolean;
  isStandaloneWidget?: boolean;
}

const HANDICAP_DOUBLE_LINE_NUMBER_OF_VISIBLE_LINES = 3;

const MarketActionsRow = ({
  market,
  showAll,
  onShowAllRunners,
  onMinimize,
  onTogglePLTable,
  isShowPlTable,
  classes,
  isOpeningSoonStatus = false,
  ignoreActions = false,
  isStandaloneWidget = false
}: MarketActionsRowProps) => {
  const { t } = useTranslation();
  const [cookies] = useCookies([CookieNames.PL_TABLE_VISIBLE]);

  const numberOfMarketSelectionsScroll = useSelector(getNumberOfMarketSelectionsScroll);
  const isMarketClosed = useSelector(getIsMarketClosed(market.marketId));
  const isMarketSuspended = useSelector(getIsMarketSuspended(market.marketId));
  const isMobile = useSelector(getIsMobile);

  const { description, runners } = market;
  const { isHandicapDoubleLine, isHandicapMarket, isEachWay, isCombinedTotal } = getMarketTypes(
    description.marketType,
    description.bettingType
  );
  const { group } = NumberOfPairs[market.description.marketType as keyof typeof NumberOfPairs] || NumberOfPairs.default;
  const totalLines = Math.floor(runners.length / group);
  const isShowTotalRecordsButton =
    totalLines > numberOfMarketSelectionsScroll ||
    (isHandicapDoubleLine &&
      (isHandicapMarket || isCombinedTotal) &&
      totalLines > HANDICAP_DOUBLE_LINE_NUMBER_OF_VISIBLE_LINES);
  const isTabDisabled = (isMarketClosed && !market.closedDate) || isMarketSuspended || isOpeningSoonStatus;
  const isPlTableDisabled = isMarketClosed || isMarketSuspended || isOpeningSoonStatus;

  const shouldDisplayTabs = isShowTotalRecordsButton && !ignoreActions;

  const eachWayBlock = isEachWay ? (
    <span className={styles.divisor}>
      {t('market.each.way.terms', {
        odds: market?.description?.eachWayDivisor,
        places: market?.numberOfWinners
      })}
    </span>
  ) : null;

  return (
    <div className="biab_market-pl-table-action">
      {(shouldDisplayTabs || isShowPlTable) && (
        <div
          className={classNames('biab_market-ah-lines-wrapper biab_handicap-market', styles.marketActions, {
            [styles.marketActions__standalone]: isStandaloneWidget,
            [classes?.actions ?? '']: classes?.actions
          })}
        >
          {shouldDisplayTabs && (
            <div
              className={classNames(styles.minimizeAndFull, {
                [mobileComponents.SWITCH_TABS]: isMobile,
                [mobileComponents.DISABLED]: isMobile && isTabDisabled
              })}
            >
              <button
                type="button"
                className={classNames(styles.marketActions__minimize, {
                  [styles.marketActions__minimize__active]: !showAll,
                  [mobileComponents.SELECTED]: isMobile && !showAll
                })}
                onClick={isTabDisabled ? undefined : onMinimize}
                disabled={isTabDisabled}
              >
                {t('market.lines.minimized')}
              </button>
              <button
                type="button"
                className={classNames(styles.marketActions__full, {
                  [styles.marketActions__full__active]: showAll,
                  [mobileComponents.SELECTED]: isMobile && showAll
                })}
                onClick={isTabDisabled ? undefined : onShowAllRunners}
                disabled={isTabDisabled}
              >
                {t('market.lines.full')}
              </button>
            </div>
          )}

          {isShowPlTable && (
            <button
              type="button"
              title={t('market.PLTable')}
              className={classNames(styles.marketActions__plTable, {
                [styles.marketActions__plTable__full]: !isShowTotalRecordsButton || ignoreActions,
                [styles.marketActions__plTable__active]: !!cookies.PL_TABLE_VISIBLE,
                [mobileComponents.BUTTON]: isMobile,
                [mobileComponents.TERTIARY]: isMobile,
                [mobileComponents.DISABLED]: isMobile && isPlTableDisabled
              })}
              onClick={isPlTableDisabled ? undefined : onTogglePLTable}
              disabled={isPlTableDisabled}
            >
              {t('market.PLTable')}
            </button>
          )}
        </div>
      )}
      {eachWayBlock}
    </div>
  );
};

export default memo(MarketActionsRow);
